import { TypographyOptions } from "@mui/material/styles/createTypography";
import { Palette, PaletteMode } from "@mui/material";
import { breakpointValues, customBreakpoints } from "./breakpoints";
import { pxToRem } from "../utils/fontSizeUtility";
import { customPalette } from "./palette";
import React from "react";

declare module "@mui/material/styles" {
  // noinspection JSUnusedGlobalSymbols
  interface TypographyVariants {
    body3: React.CSSProperties;
    detailsBarText: React.CSSProperties;
    PageHeader: React.CSSProperties;
    OptionName: React.CSSProperties;
    RadioLabel: React.CSSProperties;
    ErrorDialogTitle: React.CSSProperties;
    ErrorDialogText: React.CSSProperties;
    CodeBlock: React.CSSProperties;
    displayLarge?: React.CSSProperties;
    displayMedium?: React.CSSProperties;
    displayMediumProminent?: React.CSSProperties;
    displaySmall?: React.CSSProperties;
    headlineLarge?: React.CSSProperties;
    headlineMedium?: React.CSSProperties;
    headlineSmall?: React.CSSProperties;
    titleLarge?: React.CSSProperties;
    titleMedium?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    labelLargeProminent?: React.CSSProperties;
    labelLargeMono?: React.CSSProperties;
    labelLarge?: React.CSSProperties;
    labelMediumProminent?: React.CSSProperties;
    labelMedium?: React.CSSProperties;
    labelSmall?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyMedium?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  // noinspection JSUnusedGlobalSymbols
  interface TypographyVariantsOptions {
    body3: React.CSSProperties;
    detailsBarText?: React.CSSProperties;
    PageHeader?: React.CSSProperties;
    OptionName?: React.CSSProperties;
    RadioLabel?: React.CSSProperties;
    ErrorDialogTitle?: React.CSSProperties;
    ErrorDialogText?: React.CSSProperties;
    CodeBlock?: React.CSSProperties;
    displayLarge?: React.CSSProperties;
    displayMedium?: React.CSSProperties;
    displayMediumProminent?: React.CSSProperties;
    displaySmall?: React.CSSProperties;
    headlineLarge?: React.CSSProperties;
    headlineMedium?: React.CSSProperties;
    headlineSmall?: React.CSSProperties;
    titleLarge?: React.CSSProperties;
    titleMedium?: React.CSSProperties;
    titleSmall?: React.CSSProperties;
    labelLargeProminent?: React.CSSProperties;
    labelLargeMono?: React.CSSProperties;
    labelLarge?: React.CSSProperties;
    labelMediumProminent?: React.CSSProperties;
    labelMedium?: React.CSSProperties;
    labelSmall?: React.CSSProperties;
    bodyLarge?: React.CSSProperties;
    bodyMedium?: React.CSSProperties;
    bodySmall?: React.CSSProperties;
  }
}

// NOTE: overrides of h1-h6 based on Foundation: http://foundation.bkfs.com/#/typography
export const customTypographyOptions = (
  mode: PaletteMode,
  currentBreakpoints = customBreakpoints,
  currentPalette: Palette = customPalette(mode)
): TypographyOptions => ({
  fontFamily: ["RobotoFlex", "Arial", "Courier"].join(", "),
  h1: {
    fontSize: pxToRem(32),
    fontWeight: 700,
    lineHeight: pxToRem(40),
  },
  h2: {
    fontSize: pxToRem(28),
    fontWeight: 700,
    lineHeight: pxToRem(35),
  },
  h3: {
    fontSize: pxToRem(24),
    fontWeight: 700,
    lineHeight: pxToRem(30),
  },
  h4: {
    fontSize: pxToRem(21),
    fontWeight: 700,
    lineHeight: pxToRem(26),
  },
  h5: {
    fontSize: pxToRem(18),
    fontWeight: 700,
    lineHeight: pxToRem(22),
  },
  h6: {
    fontSize: pxToRem(16),
    fontWeight: 700,
    lineHeight: pxToRem(20),
  },
  body1: {
    fontSize: pxToRem(13),
    fontWeight: 400,
    fontVariantNumeric: "tabular-nums",
    lineHeight: pxToRem(20),
    color: currentPalette.text.primary,
    [currentBreakpoints.up(breakpointValues.md)]: {
      fontSize: pxToRem(15),
      lineHeight: pxToRem(23),
    },
  },
  body2: {
    fontSize: pxToRem(13),
    fontWeight: 400,
    lineHeight: pxToRem(20),
    color: currentPalette.text.primary,
    [currentBreakpoints.down(breakpointValues.md)]: {
      fontSize: pxToRem(14),
      lineHeight: pxToRem(21),
    },
  },
  body3: {
    fontSize: pxToRem(15),
    fontWeight: 400,
    fontVariantNumeric: "tabular-nums",
    lineHeight: pxToRem(20),
    color: currentPalette.text.secondary,
    [currentBreakpoints.up(breakpointValues.md)]: {
      fontSize: pxToRem(15),
      lineHeight: pxToRem(21),
    },
  },

  detailsBarText: {
    fontSize: pxToRem(14),
    lineHeight: pxToRem(21),
  },
  PageHeader: {
    fontSize: "32px",
    fontWeight: "bold",
    color: currentPalette.text.tertiary,
  },

  OptionName: {
    fontWeight: "bold",
    fontSize: "18px",
    marginTop: "40px",
    marginBottom: "15px",
    color: currentPalette.text.primary,
  },
  RadioLabel: {
    color: currentPalette.text.secondary,
    fontSize: "13px",
  },
});

// NOTE: overrides of h1-h6 based on Foundation: http://foundation.bkfs.com/#/typography
export const standardTypographyOptions = (
  mode: PaletteMode,
  currentBreakpoints = customBreakpoints,
  currentPalette: Palette = customPalette(mode)
): TypographyOptions => ({
  fontFamily: "RobotoFlex, Arial, Courier",
  displayLarge: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(57),
    fontWeight: 400,
    lineHeight: pxToRem(64),
  },
  displayMedium: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(45),
    fontWeight: 400,
    lineHeight: pxToRem(52),
  },
  displayMediumProminent: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(45),
    fontWeight: 600,
    lineHeight: pxToRem(52),
  },
  displaySmall: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(36),
    fontWeight: 400,
    lineHeight: pxToRem(44),
  },
  headlineLarge: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(32),
    fontWeight: 400,
    lineHeight: pxToRem(40),
  },
  headlineMedium: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(28),
    fontWeight: 400,
    lineHeight: pxToRem(36),
  },
  headlineSmall: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(24),
    fontWeight: 400,
    lineHeight: pxToRem(32),
  },
  titleLarge: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(22),
    fontWeight: 400,
    lineHeight: pxToRem(28),
  },
  titleMedium: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(16),
    fontWeight: 500,
    lineHeight: pxToRem(24),
  },
  titleSmall: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(14),
    fontWeight: 500,
    lineHeight: pxToRem(20),
  },
  labelLargeProminent: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(14),
    fontWeight: 600,
    lineHeight: pxToRem(20),
  },
  labelLargeMono: {
    fontFamily: "RobotoMono",
    fontSize: pxToRem(14),
    fontWeight: 400,
    lineHeight: pxToRem(20),
  },
  labelLarge: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(14),
    fontWeight: 500,
    lineHeight: pxToRem(20),
  },
  labelMediumProminent: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(12),
    fontWeight: 600,
    lineHeight: pxToRem(16),
  },
  labelMedium: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(12),
    fontWeight: 500,
    lineHeight: pxToRem(16),
  },
  labelSmall: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(11),
    fontWeight: 500,
    lineHeight: pxToRem(16),
  },
  bodyLarge: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(16),
    fontWeight: 400,
    lineHeight: pxToRem(24),
  },
  bodyMedium: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(14),
    fontWeight: 400,
    lineHeight: pxToRem(20),
  },
  bodySmall: {
    fontFamily: "RobotoFlex, Arial, Courier",
    fontSize: pxToRem(12),
    fontWeight: 500,
    lineHeight: pxToRem(16),
  },

  h1: {
    fontSize: pxToRem(32),
    fontWeight: 700,
    lineHeight: pxToRem(40),
  },
  h2: {
    fontSize: pxToRem(28),
    fontWeight: 700,
    lineHeight: pxToRem(35),
  },
  h3: {
    fontSize: pxToRem(24),
    fontWeight: 700,
    lineHeight: pxToRem(30),
  },
  h4: {
    fontSize: pxToRem(21),
    fontWeight: 700,
    lineHeight: pxToRem(26),
  },
  h5: {
    fontSize: pxToRem(18),
    fontWeight: 700,
    lineHeight: pxToRem(22),
  },
  h6: {
    fontSize: pxToRem(16),
    fontWeight: 700,
    lineHeight: pxToRem(20),
  },
  body1: {
    fontSize: pxToRem(13),
    fontWeight: 400,
    fontVariantNumeric: "tabular-nums",
    lineHeight: pxToRem(20),
    color: currentPalette.text.primary,
    [currentBreakpoints.up(breakpointValues.md)]: {
      fontSize: pxToRem(15),
      lineHeight: pxToRem(23),
    },
  },
  body2: {
    fontSize: pxToRem(13),
    fontWeight: 400,
    lineHeight: pxToRem(20),
    color: currentPalette.text.primary,
    [currentBreakpoints.down(breakpointValues.md)]: {
      fontSize: pxToRem(14),
      lineHeight: pxToRem(21),
    },
  },
  body3: {
    fontSize: pxToRem(15),
    fontWeight: 400,
    fontVariantNumeric: "tabular-nums",
    lineHeight: pxToRem(20),
    color: currentPalette.text.secondary,
    [currentBreakpoints.up(breakpointValues.md)]: {
      fontSize: pxToRem(15),
      lineHeight: pxToRem(21),
    },
  },

  detailsBarText: {
    fontSize: pxToRem(14),
    lineHeight: pxToRem(21),
  },
  PageHeader: {
    fontSize: "32px",
    fontWeight: "bold",
    color: currentPalette.text.tertiary,
  },

  OptionName: {
    fontWeight: "bold",
    fontSize: "18px",
    marginTop: "40px",
    marginBottom: "15px",
    color: currentPalette.text.primary,
  },
  RadioLabel: {
    color: currentPalette.text.secondary,
    fontSize: "13px",
  },
});
